import { AgentEventSchema, ServerWelcomeSchema, type AgentEvent } from "@herskin/contracts";
import { useCallback, useEffect, useRef, useState } from "react";

type RealtimeState = "connecting" | "online" | "recovering" | "offline";

const WELCOME_TIMEOUT_MS = 10_000;
const EVENT_CURSOR_STORAGE_KEY = "herskin:last-event-cursor";

export function useRealtime(
  authenticated: boolean,
  visibleSessionId: string | null,
  onEvent: (event: AgentEvent) => void,
  onSnapshotRequired: () => void,
) {
  const [state, setState] = useState<RealtimeState>("offline");
  const eventHandler = useRef(onEvent);
  const snapshotHandler = useRef(onSnapshotRequired);
  const visibleSession = useRef(visibleSessionId);
  const liveSocket = useRef<WebSocket | null>(null);
  const lastCursor = useRef(localStorage.getItem(EVENT_CURSOR_STORAGE_KEY));
  eventHandler.current = onEvent;
  snapshotHandler.current = onSnapshotRequired;
  visibleSession.current = visibleSessionId;

  /**
   * `client.hello` 除首次握手外也承担轻量应用层心跳。
   * 浏览器不能主动发送 WebSocket ping control frame；定期发送一帧小消息可以避免
   * Cloudflare/OpenResty 把完全空闲的长连接回收，同时沿用既有严格 Schema。
   */
  const sendHello = useCallback((socket: WebSocket) => {
    socket.send(
      JSON.stringify({
        type: "client.hello",
        requestId: `req_${crypto.randomUUID()}`,
        lastCursor: lastCursor.current,
        visibleSessionId: visibleSession.current,
        clientVersion: "0.1.0",
      }),
    );
  }, []);

  useEffect(() => {
    const socket = liveSocket.current;
    if (authenticated && socket?.readyState === WebSocket.OPEN) sendHello(socket);
  }, [authenticated, sendHello, visibleSessionId]);

  useEffect(() => {
    if (!authenticated) return;
    let socket: WebSocket | null = null;
    let retryTimer: number | null = null;
    let heartbeatTimer: number | null = null;
    let heartbeatDeadline: number | null = null;
    let welcomeDeadline: number | null = null;
    let cursorPersistTimer: number | null = null;
    let deltaFlushTimer: number | null = null;
    const deltaQueue: AgentEvent[] = [];
    let stopped = false;
    let attempt = 0;

    const clearHeartbeat = () => {
      if (heartbeatTimer !== null) window.clearTimeout(heartbeatTimer);
      if (heartbeatDeadline !== null) window.clearTimeout(heartbeatDeadline);
      heartbeatTimer = null;
      heartbeatDeadline = null;
    };

    const clearWelcomeDeadline = () => {
      if (welcomeDeadline !== null) window.clearTimeout(welcomeDeadline);
      welcomeDeadline = null;
    };

    const persistCursor = () => {
      if (cursorPersistTimer !== null) window.clearTimeout(cursorPersistTimer);
      cursorPersistTimer = null;
      if (lastCursor.current) {
        localStorage.setItem(EVENT_CURSOR_STORAGE_KEY, lastCursor.current);
      }
    };

    const scheduleCursorPersist = () => {
      if (cursorPersistTimer !== null) return;
      // localStorage 是同步 API。流式 delta 期间节流写入，避免阻塞浏览器主线程。
      cursorPersistTimer = window.setTimeout(persistCursor, 500);
    };

    const flushDeltas = () => {
      if (deltaFlushTimer !== null) window.clearTimeout(deltaFlushTimer);
      deltaFlushTimer = null;
      // React 会批处理同一个定时器中的状态更新，因此一批 delta 只触发一次提交。
      for (const event of deltaQueue.splice(0)) eventHandler.current(event);
    };

    const queueDelta = (event: AgentEvent) => {
      const previous = deltaQueue.at(-1);
      const sameMessage =
        previous?.type === "message.delta" &&
        previous.sessionId === event.sessionId &&
        previous.runId === event.runId &&
        previous.payload.messageId === event.payload.messageId;
      if (sameMessage && previous) {
        const previousDelta = String(previous.payload.delta ?? "");
        deltaQueue[deltaQueue.length - 1] = {
          ...event,
          payload: {
            ...event.payload,
            delta: previousDelta + String(event.payload.delta ?? ""),
          },
        };
      } else {
        deltaQueue.push(event);
      }
      if (deltaFlushTimer === null) {
        deltaFlushTimer = window.setTimeout(flushDeltas, 50);
      }
    };

    const scheduleHeartbeat = (intervalMs: number) => {
      clearHeartbeat();
      const safeInterval = Math.min(30_000, Math.max(5_000, intervalMs));
      heartbeatTimer = window.setTimeout(() => {
        if (!socket || socket.readyState !== WebSocket.OPEN) return;
        sendHello(socket);
        heartbeatDeadline = window.setTimeout(() => {
          // 服务器在正常情况下会用 server.welcome 回应 hello。
          socket?.close(4408, "heartbeat timeout");
        }, 10_000);
      }, safeInterval);
    };

    const connect = () => {
      if (stopped) return;
      setState(attempt ? "recovering" : "connecting");
      const protocol = location.protocol === "https:" ? "wss:" : "ws:";
      const candidate = new WebSocket(`${protocol}//${location.host}/api/realtime`);
      socket = candidate;
      liveSocket.current = candidate;
      candidate.addEventListener("open", () => {
        sendHello(candidate);
        clearWelcomeDeadline();
        welcomeDeadline = window.setTimeout(() => {
          if (candidate.readyState !== WebSocket.OPEN || liveSocket.current !== candidate) return;
          // reload 会携带持久化 cursor 进入 replay 路径。若握手已 open 却迟迟没有
          // 可验证 welcome，丢弃 cursor 并退回 REST Snapshot，避免永久卡在 connecting。
          lastCursor.current = null;
          localStorage.removeItem(EVENT_CURSOR_STORAGE_KEY);
          snapshotHandler.current();
          candidate.close(4408, "server welcome timeout");
        }, WELCOME_TIMEOUT_MS);
      });
      candidate.addEventListener("message", (message) => {
        let body: unknown;
        try {
          body = JSON.parse(String(message.data));
        } catch {
          return;
        }
        const welcome = ServerWelcomeSchema.safeParse(body);
        if (welcome.success) {
          clearWelcomeDeadline();
          attempt = 0;
          setState("online");
          scheduleHeartbeat(welcome.data.heartbeatMs);
          if (welcome.data.resume === "snapshot_required") snapshotHandler.current();
          return;
        }
        const event = AgentEventSchema.safeParse(body);
        if (!event.success) return;
        lastCursor.current = event.data.cursor;
        scheduleCursorPersist();
        if (event.data.type === "message.delta") {
          queueDelta(event.data);
        } else {
          // completed 必须排在此前 delta 之后，否则旧 delta 可能覆盖最终正文。
          flushDeltas();
          eventHandler.current(event.data);
        }
      });
      candidate.addEventListener("close", (close) => {
        clearWelcomeDeadline();
        clearHeartbeat();
        flushDeltas();
        persistCursor();
        if (socket === candidate) socket = null;
        if (liveSocket.current === candidate) liveSocket.current = null;
        if (stopped || close.code === 4401) {
          setState("offline");
          return;
        }
        attempt += 1;
        setState("recovering");
        retryTimer = window.setTimeout(connect, Math.min(30_000, 1000 * 2 ** attempt));
      });
      candidate.addEventListener("error", () => candidate.close());
    };
    connect();
    return () => {
      stopped = true;
      if (retryTimer !== null) window.clearTimeout(retryTimer);
      clearWelcomeDeadline();
      clearHeartbeat();
      if (deltaFlushTimer !== null) window.clearTimeout(deltaFlushTimer);
      deltaFlushTimer = null;
      deltaQueue.length = 0;
      persistCursor();
      socket?.close(1000, "view changed");
      liveSocket.current = null;
    };
  }, [authenticated, sendHello]);

  return state;
}
